import { useState, useMemo, useEffect } from 'react'
import { Dialog, DialogBody, DialogActions, DialogTitle, DialogDescription } from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem } from '@/components/catalyst-ui/dropdown'
import { Avatar } from '@/components/catalyst-ui/avatar'
import { GlobeAltIcon, XMarkIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import { Text } from '@/components/catalyst-ui/text'
import { hasFeature } from '@/lib/hasFeature'

function formatRelativeTime(date) {
  const now = new Date()
  const diffMs = now - new Date(date)
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
  if (diffYears < 1) return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`
}

function ProjectOption({ project, showTime = true, showArrow = false }) {
  const favicon = project.faviconUrl || project.favIcon || project.thumbnail
  const initials = project.name?.charAt(0)?.toUpperCase() || 'P'
  const updatedAt = project?.timestamps?.updatedAt || project?.updatedAt
  const updatedLabel = updatedAt ? formatRelativeTime(updatedAt) : null
  const projectName = project.name || 'Unnamed Project'
  const plan = (project.order?.paid ? project.order?.plan : null) || project.plan || 'Free'
  const isPaid = project.order?.paid

  return (
    <div className="flex items-center justify-between w-full gap-2 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        {favicon ? (
          <img
            src={favicon}
            alt={projectName}
            className="h-[20px] w-[20px] rounded-full border border-[#89898A] bg-white object-cover shrink-0"
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.removeAttribute('src')
            }}
          />
        ) : (
          <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full bg-[#89898A] text-[10px] uppercase font-medium text-[#FFFFFF]">
            {initials}
          </span>
        )}
        <span className="text-sm font-medium text-[#09090B] truncate">{projectName}</span>
        <span className={`text-xs font-medium px-2.5 py-px rounded-full shrink-0 ${isPaid ? 'bg-[#71717A] text-white' : 'bg-[#F4F4F5] text-[#71717A]'}`}>
          {plan}
        </span>
      </div>
      {(showTime && updatedLabel) || showArrow ? (
        <div className="flex items-center justify-end gap-2 shrink-0">
          {showTime && updatedLabel && (
            <span className="text-sm scale-90 sm:scale-100 text-right font-medium text-[#71717A]">{updatedLabel}</span>
          )}
          {showArrow && (
            <ChevronUpDownIcon className="w-4 h-4 text-gray-500" />
          )}
        </div>
      ) : null}
    </div>
  )
}

export default function ConnectProjectDropdown({
  projects = [],
  onSelect,
  buttonText = 'Connect project',
  buttonClassName = 'text-black underline font-medium cursor-pointer hover:text-gray-700',
  open: controlledOpen,
  onOpenChange,
  domainName,
  description,
  skipGating = false,
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setIsOpen = (value) => {
    if (controlledOpen === undefined) {
      setInternalOpen(value)
    }
    if (onOpenChange) {
      onOpenChange(value)
    }
  }
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [projectSearch, setProjectSearch] = useState('')

  const sortedProjects = useMemo(() => {
    const list = Array.isArray(projects) ? projects.slice() : []
    return list.sort((a, b) => {
      const aTime = new Date(a?.timestamps?.updatedAt || a?.updatedAt || 0).getTime()
      const bTime = new Date(b?.timestamps?.updatedAt || b?.updatedAt || 0).getTime()
      return bTime - aTime
    })
  }, [projects])

  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return sortedProjects
    const query = projectSearch.trim().toLowerCase()
    return sortedProjects.filter((project) =>
      (project.name || '').toLowerCase().includes(query)
    )
  }, [projectSearch, sortedProjects])

  //ADD LIMIT IF REQUIRED !!!
  // const visibleProjects = filteredProjects.slice(0, 6)
  const visibleProjects = filteredProjects
// 
  const selectedProject = useMemo(() => {
    return sortedProjects.find(p => (p.id || p._id) === selectedProjectId)
  }, [sortedProjects, selectedProjectId])

  const handleProjectSelect = (project) => {
    setSelectedProjectId(project.id || project._id)
  }

  const handleConnect = () => {
    if (selectedProject && onSelect) {
      onSelect(selectedProject)
      setIsOpen(false)
      setSelectedProjectId('')
      setProjectSearch('')
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedProjectId('')
    setProjectSearch('')
  }

  useEffect(() => {
    if (controlledOpen === true && !internalOpen) {
      setInternalOpen(true)
    }
  }, [controlledOpen, internalOpen])

  return (
    <>
      {buttonText && (
        <button
          onClick={() => skipGating ? setIsOpen(true) : hasFeature('DOMAIN_CONNECTION', () => setIsOpen(true), ['limitPremium'])}
          className={buttonClassName}
        >
          {buttonText}
        </button>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        className="!rounded-xl p-5"
      >
        <div className="flex items-center justify-between">
          <DialogTitle className="!text-lg !font-semibold !text-[#09090B]">Connect Project</DialogTitle>
          <button
            onClick={handleClose}
            className="text-black hover:text-zinc-600 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded-full"
          >
            <XMarkIcon className="w-5 h-5 -mt-6 -mr-2.5" />
          </button>
        </div>
        <DialogBody className="!mt-2">
          <DialogDescription className="!text-sm !font-medium !text-[#71717A]">
            {description || (domainName ? (
              <>Select a project to connect <strong className="text-black font-bold">{domainName}</strong> to</>
            ) : (
              'Select a project to connect to this domain.'
            ))}
          </DialogDescription>
          
          <div className="mt-4">
            {sortedProjects.length === 0 ? (
              <div className="px-4 py-3 flex items-center gap-2 bg-gray-50 rounded-md">
                <Text className="text-sm text-gray-500">
                  No projects available
                </Text>
              </div>
            ) : (
              <Dropdown>
                {({ open }) => (
                  <>
                    <DropdownButton
                      as={Button}
                      outline
                      className="w-full !flex justify-between border border-gray-300 rounded-md bg-white hover:bg-gray-50 px-3 py-2 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                    >
                      {selectedProject ? (
                        <ProjectOption project={selectedProject} showArrow={true} />
                      ) : (
                        <div className="flex items-center justify-between w-full">
                          <span className="text-sm !font-medium text-black">Select a project</span>
                          <ChevronUpDownIcon className="w-4 h-4 text-gray-500 shrink-0" />
                        </div>
                      )}
                    </DropdownButton>
                    <DropdownMenu className="!w-[calc(100vw-2.5rem)] !bg-[#FAFAFA] !flex !flex-col !p-1 !overflow-hidden sm:!w-[29.5rem] !rounded-lg !max-h-[290px] !overflow-y-auto">
                      <div className="!w-full mb-1">
                        <div className="relative w-full">
                          <input
                            value={projectSearch}
                            onChange={(e) => setProjectSearch(e.target.value)}
                            placeholder="Search sites"
                            className="w-full rounded-lg border border-[#E4E4E7] bg-white py-2 pr-10 pl-3 text-sm font-medium text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          />
                          {projectSearch ? (
                            <button
                              type="button"
                              onClick={() => setProjectSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#71717A] hover:text-[#18181B] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              aria-label="Clear search"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          ) : (
                            <MagnifyingGlassIcon className="w-4 h-4 text-[#A1A1AA] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col w-full">
                        {visibleProjects.length === 0 ? (
                          <div className="px-5 py-3 text-sm text-[#71717A]">No sites found</div>
                        ) : (
                          visibleProjects.map((project) => {
                            const key = project.id || project._id
                            const isSelected = selectedProjectId === key
                            return (
                              <DropdownItem
                                as="button"
                                key={key}
                                onClick={() => handleProjectSelect(project)}
                                className={`!w-full !px-5 !py-2.5 rounded-lg transition-colors  hover:!bg-[#EBEBEB] ${isSelected ? '!bg-[#CBCBCB]/40' : ''}`}
                              >
                                <ProjectOption project={project} />
                              </DropdownItem>
                            )
                          })
                        )}
                      </div>
                    </DropdownMenu>
                  </>
                )}
              </Dropdown>
            )}
          </div>
        </DialogBody>
        <DialogActions className="!mt-4 !flex !flex-col sm:!flex-row !gap-3">
          <Button
            outline
            onClick={handleClose}
            className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={!selectedProjectId}
            color="dark"
            className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
